/**
 * Theme selector for the plugin builder.
 * Browsable catalog of themes with visual previews,
 * custom theme builder, and font pairing selection.
 */

import { useState, useMemo, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { Sparkles, Check, Palette, Type, Paintbrush } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  THEME_CATALOG,
  FONT_PAIRINGS,
  type ThemeCatalogEntry,
  type FiasTheme,
  type FontPairing,
} from '@fias/arche-sdk';

interface ThemeSelectorProps {
  selectedThemeId: string;
  onThemeChange: (themeId: string, customTheme?: FiasTheme) => void;
  selectedFontPairingId: string;
  onFontPairingChange: (fontPairingId: string) => void;
  /** Compact mode for the welcome page (fewer columns, smaller cards) */
  compact?: boolean;
  className?: string;
}

/** Mini visual preview of a theme */
function ThemePreviewCard({
  entry,
  isSelected,
  onClick,
  compact,
}: {
  entry: ThemeCatalogEntry;
  isSelected: boolean;
  onClick: () => void;
  compact?: boolean;
}) {
  const { theme, name, source } = entry;
  const isSpecial =
    source === 'ai' || source === 'none' || entry.id === 'custom' || entry.id === '_catalog';

  const specialIcon =
    entry.id === '_catalog'
      ? Palette
      : source === 'ai'
        ? Sparkles
        : entry.id === 'custom'
          ? Paintbrush
          : Paintbrush;
  const specialLabel =
    entry.id === '_catalog'
      ? 'Browse theme catalog'
      : source === 'ai'
        ? 'AI picks the best theme'
        : entry.id === 'custom'
          ? 'Build your own theme'
          : 'AI builds its own design';
  const SpecialIcon = specialIcon;

  return (
    <button
      onClick={onClick}
      className={cn(
        'relative rounded-lg border-2 transition-all text-left overflow-hidden group',
        compact ? 'w-full' : 'w-full',
        isSelected
          ? 'border-primary ring-2 ring-primary/20'
          : 'border-transparent hover:border-muted-foreground/30',
      )}
    >
      {/* Preview area */}
      <div
        className={cn('p-3', compact ? 'h-[100px]' : 'h-[120px]')}
        style={{ backgroundColor: theme.colors.background }}
      >
        {isSpecial ? (
          <div className="flex flex-col items-center justify-center h-full gap-2">
            <SpecialIcon className="h-6 w-6" style={{ color: entry.preview.colors[0] }} />
            <span
              className="text-xs font-medium text-center leading-tight"
              style={{ color: theme.colors.text }}
            >
              {specialLabel}
            </span>
          </div>
        ) : (
          <div className="flex flex-col h-full justify-between">
            {/* Mock heading */}
            <div>
              <div
                className="text-xs font-semibold mb-1 truncate"
                style={{
                  color: theme.colors.text,
                  fontFamily: theme.fonts.heading,
                }}
              >
                {name}
              </div>
              <div
                className="text-[10px] leading-tight"
                style={{ color: theme.colors.textSecondary }}
              >
                Sample body text
              </div>
            </div>
            {/* Mock UI elements */}
            <div className="flex items-center gap-1.5">
              <div
                className="px-2 py-0.5 text-[10px] font-medium"
                style={{
                  backgroundColor: theme.colors.primary,
                  color: theme.colors.primaryText,
                  borderRadius: theme.components.buttonRadius,
                }}
              >
                Button
              </div>
              <div
                className="px-2 py-0.5 text-[10px]"
                style={{
                  backgroundColor: theme.colors.accent,
                  color: theme.colors.primaryText,
                  borderRadius: theme.components.buttonRadius,
                }}
              >
                Accent
              </div>
              <div
                className="h-4 flex-1"
                style={{
                  backgroundColor: theme.colors.surface,
                  borderRadius: theme.components.cardRadius,
                  border: `${theme.components.borderWidth} solid ${theme.colors.border}`,
                }}
              />
            </div>
          </div>
        )}
      </div>
      {/* Label */}
      <div className="px-2 py-1.5 bg-background border-t text-center">
        <span className="text-[11px] font-medium truncate block">{name}</span>
      </div>
      {/* Selected check */}
      {isSelected && (
        <div className="absolute top-1.5 right-1.5 bg-primary text-primary-foreground rounded-full p-0.5">
          <Check className="h-3 w-3" />
        </div>
      )}
    </button>
  );
}

/** Font pairing preview */
function FontPairingCard({
  pairing,
  isSelected,
  onClick,
}: {
  pairing: FontPairing;
  isSelected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full rounded-lg border-2 p-3 text-left transition-all',
        isSelected
          ? 'border-primary ring-2 ring-primary/20'
          : 'border-transparent hover:border-muted-foreground/30',
      )}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium">{pairing.name}</span>
        {isSelected && (
          <div className="bg-primary text-primary-foreground rounded-full p-0.5">
            <Check className="h-3 w-3" />
          </div>
        )}
      </div>
      <div style={{ fontFamily: pairing.heading }} className="text-2xl font-bold mb-2">
        The Quick Brown Fox
      </div>
      <div style={{ fontFamily: pairing.body }} className="text-base text-muted-foreground mb-1">
        Jumps over the lazy dog. This is body text at a comfortable reading size.
      </div>
      <div style={{ fontFamily: pairing.mono }} className="text-sm text-muted-foreground font-mono">
        const code = "monospace";
      </div>
    </button>
  );
}

/** Simple custom theme builder */
function CustomThemeBuilder({ onApply }: { onApply: (theme: FiasTheme) => void }) {
  const [primaryColor, setPrimaryColor] = useState('#6C63FF');
  const [mode, setMode] = useState<'light' | 'dark'>('dark');
  const [borderRadius, setBorderRadius] = useState(8);
  const [borderWidth, setBorderWidth] = useState(1);
  const [shadowIntensity, setShadowIntensity] = useState(1);

  const customTheme = useMemo((): FiasTheme => {
    // Generate a palette from the primary color
    const hex = primaryColor;
    const isDark = mode === 'dark';

    // Simple palette derivation from primary
    const bg = isDark ? '#0a0a0a' : '#ffffff';
    const surface = isDark ? '#171717' : '#f5f5f5';
    const card = isDark ? '#1c1c1c' : '#ffffff';
    const text = isDark ? '#fafafa' : '#171717';
    const textSec = isDark ? '#a1a1aa' : '#71717a';
    const border = isDark ? '#27272a' : '#e4e4e7';
    const muted = isDark ? '#27272a' : '#f4f4f5';
    const mutedText = isDark ? '#71717a' : '#a1a1aa';

    const radiusPx = `${borderRadius}px`;
    const bw = `${borderWidth}px`;

    const shadows = [
      { sm: 'none', md: 'none', lg: 'none' },
      {
        sm: '0 1px 2px rgba(0,0,0,0.05)',
        md: '0 4px 6px -1px rgba(0,0,0,0.1)',
        lg: '0 10px 15px -3px rgba(0,0,0,0.1)',
      },
      {
        sm: '0 1px 3px rgba(0,0,0,0.12)',
        md: '0 4px 12px rgba(0,0,0,0.15)',
        lg: '0 10px 25px rgba(0,0,0,0.2)',
      },
    ];
    const shadow = shadows[shadowIntensity];

    return {
      mode,
      colors: {
        primary: hex,
        primaryText: isDark ? '#000000' : '#ffffff',
        secondary: isDark ? '#27272a' : '#e4e4e7',
        accent: hex,
        background: bg,
        surface,
        card,
        cardText: text,
        text,
        textSecondary: textSec,
        muted,
        mutedText,
        border,
        error: '#ef4444',
        warning: '#f59e0b',
        success: '#22c55e',
        info: '#3b82f6',
      },
      spacing: { xs: '4px', sm: '8px', md: '16px', lg: '24px', xl: '32px' },
      fonts: {
        body: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        heading: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        mono: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace",
      },
      components: {
        borderRadius: radiusPx,
        buttonRadius: borderRadius >= 20 ? '9999px' : radiusPx,
        cardRadius: radiusPx,
        inputRadius: `${Math.max(borderRadius - 2, 0)}px`,
        shadowSm: shadow.sm,
        shadowMd: shadow.md,
        shadowLg: shadow.lg,
        borderWidth: bw,
      },
    };
  }, [primaryColor, mode, borderRadius, borderWidth, shadowIntensity]);

  // Auto-apply on any change — use a ref for onApply to avoid re-triggering
  const onApplyRef = useRef(onApply);
  onApplyRef.current = onApply;
  useEffect(() => {
    onApplyRef.current(customTheme);
  }, [customTheme]);

  return (
    <div className="space-y-4">
      {/* Preview */}
      <div
        className="rounded-lg p-4 space-y-3"
        style={{
          backgroundColor: customTheme.colors.background,
          border: `${customTheme.components.borderWidth} solid ${customTheme.colors.border}`,
        }}
      >
        <div
          style={{ color: customTheme.colors.text, fontFamily: customTheme.fonts.heading }}
          className="font-semibold text-sm"
        >
          Preview
        </div>
        <div className="flex gap-2">
          <div
            className="px-3 py-1.5 text-xs font-medium"
            style={{
              backgroundColor: customTheme.colors.primary,
              color: customTheme.colors.primaryText,
              borderRadius: customTheme.components.buttonRadius,
              boxShadow: customTheme.components.shadowSm,
            }}
          >
            Primary Button
          </div>
          <div
            className="px-3 py-1.5 text-xs"
            style={{
              backgroundColor: customTheme.colors.surface,
              color: customTheme.colors.text,
              borderRadius: customTheme.components.buttonRadius,
              border: `${customTheme.components.borderWidth} solid ${customTheme.colors.border}`,
            }}
          >
            Secondary
          </div>
        </div>
        <div
          className="p-3 text-xs"
          style={{
            backgroundColor: customTheme.colors.card,
            color: customTheme.colors.cardText,
            borderRadius: customTheme.components.cardRadius,
            border: `${customTheme.components.borderWidth} solid ${customTheme.colors.border}`,
            boxShadow: customTheme.components.shadowMd,
          }}
        >
          <div className="font-medium mb-1">Card component</div>
          <div style={{ color: customTheme.colors.textSecondary }}>
            Secondary text in a card with shadow and border radius.
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <Label className="text-xs w-24 shrink-0">Primary Color</Label>
          <input
            type="color"
            value={primaryColor}
            onChange={(e) => setPrimaryColor(e.target.value)}
            className="h-8 w-12 rounded border cursor-pointer"
          />
          <span className="text-xs text-muted-foreground font-mono">{primaryColor}</span>
        </div>

        <div className="flex items-center gap-3">
          <Label className="text-xs w-24 shrink-0">Mode</Label>
          <Select value={mode} onValueChange={(v) => setMode(v as 'light' | 'dark')}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="dark">Dark</SelectItem>
              <SelectItem value="light">Light</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-3">
          <Label className="text-xs w-24 shrink-0">Roundness</Label>
          <Slider
            value={[borderRadius]}
            onValueChange={([v]) => setBorderRadius(v)}
            min={0}
            max={24}
            step={2}
            className="flex-1"
          />
          <span className="text-xs text-muted-foreground w-8 text-right">{borderRadius}px</span>
        </div>

        <div className="flex items-center gap-3">
          <Label className="text-xs w-24 shrink-0">Border Width</Label>
          <Slider
            value={[borderWidth]}
            onValueChange={([v]) => setBorderWidth(v)}
            min={0}
            max={3}
            step={1}
            className="flex-1"
          />
          <span className="text-xs text-muted-foreground w-8 text-right">{borderWidth}px</span>
        </div>

        <div className="flex items-center gap-3">
          <Label className="text-xs w-24 shrink-0">Shadows</Label>
          <Select
            value={String(shadowIntensity)}
            onValueChange={(v) => setShadowIntensity(Number(v))}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="0">None</SelectItem>
              <SelectItem value="1">Subtle</SelectItem>
              <SelectItem value="2">Pronounced</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

export function ThemeSelector({
  selectedThemeId,
  onThemeChange,
  selectedFontPairingId,
  onFontPairingChange,
  compact = false,
  className,
}: ThemeSelectorProps) {
  // Determine which top-level mode is active
  const isAiAuto = selectedThemeId === 'ai-auto';
  const isNoTheme = selectedThemeId === 'no-theme';
  const isCustom = selectedThemeId === 'custom';
  const isCatalog = !isAiAuto && !isNoTheme && !isCustom;

  const darkThemes = THEME_CATALOG.filter(
    (t) => t.mode === 'dark' && t.source !== 'ai' && t.source !== 'none',
  );
  const lightThemes = THEME_CATALOG.filter(
    (t) => t.mode === 'light' && t.source !== 'ai' && t.source !== 'none',
  );

  // Synthetic entries for the top-level choices
  const aiAutoEntry = THEME_CATALOG.find((t) => t.id === 'ai-auto')!;
  const noThemeEntry = THEME_CATALOG.find((t) => t.id === 'no-theme')!;
  const catalogEntry: ThemeCatalogEntry = {
    id: '_catalog',
    name: 'Choose a Theme',
    source: 'fias',
    mode: 'dark',
    theme: THEME_CATALOG.find((t) => t.id === 'fias-dark')!.theme,
    preview: { colors: ['#ffffff', '#1f1f1f', '#0a0a0a', '#3b82f6'] },
  };
  const customEntry: ThemeCatalogEntry = {
    id: 'custom',
    name: 'Build Your Own',
    source: 'community',
    mode: 'dark',
    theme: THEME_CATALOG.find((t) => t.id === 'fias-dark')!.theme,
    preview: { colors: ['#8b5cf6', '#06b6d4', '#1e1b4b', '#f472b6'] },
  };

  return (
    <div className={cn('flex flex-col', className)}>
      <Tabs defaultValue="themes" className="flex flex-col min-h-0">
        <TabsList className="w-full shrink-0">
          <TabsTrigger value="themes" className="flex-1 gap-1.5">
            <Palette className="h-3.5 w-3.5" />
            Themes
          </TabsTrigger>
          <TabsTrigger value="fonts" className="flex-1 gap-1.5">
            <Type className="h-3.5 w-3.5" />
            Fonts
          </TabsTrigger>
        </TabsList>

        <TabsContent value="themes" className="space-y-4 mt-3 flex-1 overflow-y-auto min-h-0">
          {/* Top-level choices */}
          <div className={cn('grid gap-2', compact ? 'grid-cols-2' : 'grid-cols-2 md:grid-cols-4')}>
            <ThemePreviewCard
              entry={catalogEntry}
              isSelected={isCatalog}
              onClick={() => onThemeChange('fias-dark')}
              compact={compact}
            />
            <ThemePreviewCard
              entry={aiAutoEntry}
              isSelected={isAiAuto}
              onClick={() => onThemeChange('ai-auto')}
              compact={compact}
            />
            <ThemePreviewCard
              entry={noThemeEntry}
              isSelected={isNoTheme}
              onClick={() => onThemeChange('no-theme')}
              compact={compact}
            />
            <ThemePreviewCard
              entry={customEntry}
              isSelected={isCustom}
              onClick={() => onThemeChange('custom')}
              compact={compact}
            />
          </div>

          {/* Separator + content for the selected choice */}
          {(isCatalog || isCustom) && (
            <div className="relative py-1 mt-12">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t-4 border-primary/30" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-background px-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {isCustom ? 'Customize' : 'Select a Theme'}
                </span>
              </div>
            </div>
          )}

          {/* Catalog themes (shown when a catalog theme is selected) */}
          {isCatalog && (
            <>
              <div>
                <h4 className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wider">
                  Dark Themes
                </h4>
                <div
                  className={cn(
                    'grid gap-2',
                    compact ? 'grid-cols-3' : 'grid-cols-3 md:grid-cols-4',
                  )}
                >
                  {darkThemes.map((entry) => (
                    <ThemePreviewCard
                      key={entry.id}
                      entry={entry}
                      isSelected={selectedThemeId === entry.id}
                      onClick={() => onThemeChange(entry.id)}
                      compact={compact}
                    />
                  ))}
                </div>
              </div>
              <div>
                <h4 className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wider">
                  Light Themes
                </h4>
                <div
                  className={cn(
                    'grid gap-2',
                    compact ? 'grid-cols-3' : 'grid-cols-3 md:grid-cols-4',
                  )}
                >
                  {lightThemes.map((entry) => (
                    <ThemePreviewCard
                      key={entry.id}
                      entry={entry}
                      isSelected={selectedThemeId === entry.id}
                      onClick={() => onThemeChange(entry.id)}
                      compact={compact}
                    />
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Custom theme builder (shown when Build Your Own is selected) */}
          {isCustom && <CustomThemeBuilder onApply={(theme) => onThemeChange('custom', theme)} />}
        </TabsContent>

        <TabsContent value="fonts" className="mt-3 flex-1 overflow-y-auto min-h-0">
          <div className="grid gap-2">
            {FONT_PAIRINGS.map((pairing) => (
              <FontPairingCard
                key={pairing.id}
                pairing={pairing}
                isSelected={selectedFontPairingId === pairing.id}
                onClick={() => onFontPairingChange(pairing.id)}
              />
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
